import {
  DialogContent,
  Dialog,
  DialogProps,
  useEventCallback,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import { styled } from '@mui/styles';
import { PmiButton } from 'shell/components/shared/button';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import {
  FilterDomesticPartnershipVM,
  TableDomesticPartnershipBodyVM,
  TableDomesticPartnershipVM,
} from 'practice/view-models/practice-edit-residence';
import Filter from './filter';
import TableDomesticPartnership from './table-domestic-partnership';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponseAnprData } from 'shell/models/person';
import { HttpResponse } from 'shell/models/http-response';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { AxiosResponse } from 'axios';
import { FamilyCoexistence } from 'shell/models/person/family';
import { Residence } from 'shell/models/person/residence';
import { ItemVM } from 'shell/view-models/item';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { FormActualResidenceVM } from 'practice/view-models/pratice-actual-residence';
import { transformActualResidencePractice } from 'practice/utils/transform-residence-vm';
import { ISTAT_MILANO } from 'shell/constants/istat-milano';

export const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {
        minWidth: 'unset',
        maxWidth: '100%',
      }
    : {
        minWidth: '50rem',
        maxWidth: '1100px',
      },
}));

interface ModalDomesticPartnershipProps {
  open: boolean;
  onClose: (open: boolean) => void;
  onConfirm: (table?: TableDomesticPartnershipVM, residence?: FormActualResidenceVM) => void;
  idFamilyAnpr?: string;
  typeRegister?: string;
}

type CohabitationResponse = { body: Array<TableDomesticPartnershipBodyVM>; problems: SaveDataAnpr };

export type ModalDomesticVM = {
  filterDomesticPartnership?: FilterDomesticPartnershipVM;
  tableDomesticPartnership?: TableDomesticPartnershipVM;
};

const ModalDomesticPartnershipComponent = ({
  open,
  onClose,
  onConfirm,
  idFamilyAnpr,
  typeRegister,
}: ModalDomesticPartnershipProps): React.ReactElement => {
  const [withSearch, setWithSearch] = useState<boolean>(false);

  const onSave = useEventCallback((values: ModalDomesticVM) => {
    if (!withSearch) {
      // se si chiude senza ricerca
      onClose(true);

      return;
    }

    const item = values.tableDomesticPartnership?.body?.find((element) => element.isActive);

    if (!item) {
      return;
    }

    let residence: FormActualResidenceVM = transformActualResidencePractice(item.residence);

    residence = {
      ...residence,
      nomeConvivenza: item.nameDomesticPartnership || '',
    };

    onConfirm(
      { ...values.tableDomesticPartnership, body: [item] } as TableDomesticPartnershipVM,
      residence
    );
  });

  const formContext = useFormik<ModalDomesticVM>({
    initialValues: {
      filterDomesticPartnership: {
        istat: ISTAT_MILANO,
      },
    },
    onSubmit: onSave,
    enableReinitialize: true,
  });

  const [anpr, getAnpr] = useHttpFetch<HttpResponse<ResponseAnprData>, CohabitationResponse>(
    `${__PERSON_API_URL__}/persona/convivenza/ricerca`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            identificativoANPR:
              idFamilyAnpr || formContext.values.filterDomesticPartnership?.IdCardAnpr || undefined,
            tipoScheda: '2', // fixed 2 = Convivenza
            idFamigliaConvivenzaComune: {
              idFamigliaConvivenza:
                formContext.values.filterDomesticPartnership?.IdCardMunicipality || undefined,
              idFamigliaConvivenzaComuneIstat:
                formContext.values.filterDomesticPartnership?.istat || undefined,
            },
          },
          datiRichiesta: {
            ricercaNazionale: '0', // fixed
            dataRiferimentoRichiesta: formContext.values.filterDomesticPartnership?.date
              ? formContext.values.filterDomesticPartnership?.date.toFormat('yyyy-MM-dd')
              : undefined,
            comuneResidenza: {
              siglaProvinciaIstat: 'MI',
              nomeComune: 'MILANO',
              codiceIstat: ISTAT_MILANO,
            }, // fixed
            datiAnagraficiRichiesti: ['02', '21', '22'], // fixed
            schedaAnagraficaRichiesta: '4', // fixed
          },
        },
      },
      transformer: (response: AxiosResponse<HttpResponse<ResponseAnprData>>) => {
        const data: FamilyCoexistence =
          (response.data.payload?.risposta3002OK?.listaSoggetti?.datiSoggetto || [])[0]
            ?.famigliaConvivenza || {};

        const residenza: Residence =
          ((response.data.payload?.risposta3002OK?.listaSoggetti?.datiSoggetto || [])[0]
            ?.residenza || [])[0] || {};

        if ((response.data.payload?.rispostaKO?.listaErrori?.errore || [])?.length > 0) {
          // mostra errors
          setVisibility({
            error: true,
            warning: false,
          });
        }

        if ((response.data.payload?.risposta3002OK?.listaAnomalie?.errore || [])?.length > 0) {
          // mostra warnings
          setVisibility({
            error: false,
            warning: true,
          });
        }

        return {
          body: [
            {
              IdCardMunicipality: data.idFamigliaConvivenzaComune?.idFamigliaConvivenza || '',
              codeIstat: data.idFamigliaConvivenzaComune?.idFamigliaConvivenzaComuneIstat || '',
              idAnpr: data.idFamigliaConvivenzaANPR || '',
              nameDomesticPartnership: data.denominazioneConvivenza || '',
              address: `${residenza.indirizzo?.toponimo?.denominazioneToponimo || ''}, ${
                residenza.indirizzo?.numeroCivico?.numero || ''
              } ${
                residenza.indirizzo?.numeroCivico?.esponente1 ||
                residenza.indirizzo?.numeroCivico?.lettera
                  ? `/${residenza.indirizzo?.numeroCivico?.esponente1 || ''}${
                      residenza.indirizzo?.numeroCivico?.lettera || ''
                    }`
                  : ''
              }`,
              residence: residenza,
            },
          ],
          problems: {
            warnings: (response.data.payload?.risposta3002OK?.listaAnomalie?.errore || []).map(
              (warning) => ({
                id: warning.codiceErroreAnomalia,
                value: warning.testoErroreAnomalia,
              })
            ),
            errors: (response.data.payload?.rispostaKO?.listaErrori?.errore || []).map((error) => ({
              id: error.codiceErroreAnomalia,
              value: error.testoErroreAnomalia,
            })),
          },
        };
      },
    }
  );

  const onSearch = useEventCallback(() => {
    const hasMunicipalityData =
      (formContext.values.filterDomesticPartnership?.IdCardMunicipality &&
        formContext.values.filterDomesticPartnership?.istat) ||
      formContext.values.filterDomesticPartnership?.cohabitationName?.id;

    const idFamigliaConvivenza =
      formContext.values.filterDomesticPartnership?.IdCardMunicipality ||
      formContext.values.filterDomesticPartnership?.cohabitationName?.id ||
      undefined;
    getAnpr({
      data: {
        nome: formContext.values.filterDomesticPartnership?.cohabitationName?.value || undefined,
        specieVia: formContext.values.filterDomesticPartnership?.kindStreet || undefined,
        via:
          formContext.values.filterDomesticPartnership?.cohabitationStreet?.id?.toString() ||
          undefined,
        criteriRicerca: {
          identificativoANPR:
            idFamilyAnpr || formContext.values.filterDomesticPartnership?.IdCardAnpr || undefined,
          tipoScheda: '2', // fixed 2 = Convivenzabl
          idFamigliaConvivenzaComune: hasMunicipalityData
            ? {
                idFamigliaConvivenza,
                idFamigliaConvivenzaComuneIstat:
                  formContext.values.filterDomesticPartnership?.istat || undefined,
              }
            : undefined,
        },
        datiRichiesta: {
          ricercaNazionale: formContext.values.filterDomesticPartnership?.isNationalResearch
            ? '1'
            : '0', // fixed
          dataRiferimentoRichiesta: formContext.values.filterDomesticPartnership?.date
            ? formContext.values.filterDomesticPartnership?.date.toFormat('yyyy-MM-dd')
            : undefined,
          comuneResidenza: {
            siglaProvinciaIstat:
              formContext.values.filterDomesticPartnership?.municipality?.province || 'MI',
            nomeComune:
              formContext.values.filterDomesticPartnership?.municipality?.description || 'MILANO',
            codiceIstat:
              formContext.values.filterDomesticPartnership?.municipality?.id || ISTAT_MILANO,
          }, // fixed
          datiAnagraficiRichiesti: ['02', '21', '22'], // fixed
          schedaAnagraficaRichiesta: '4', // fixed
        },
      },
    });
  });

  const castedAnprData = anpr.data as CohabitationResponse;

  useEffect(() => {
    if ((castedAnprData?.body || []).length > 0) {
      // true se è ststa effettuata la ricerca
      setWithSearch(true);
    }
  }, [castedAnprData]);

  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const onVisibility = useCallback(
    (openVisibility, type) => {
      const visibilityMessage = type === 'error' && !openVisibility ? undefined : openVisibility;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedAnprData?.problems?.errors && castedAnprData?.problems?.errors?.length) {
      errorToVisualize.title =
        castedAnprData?.problems?.errors &&
        castedAnprData?.problems?.errors?.length &&
        castedAnprData?.problems?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.problems?.errors?.length} errori`
          : `C'è 1 errore`;
      errorToVisualize.messages = castedAnprData?.problems?.errors;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData]);

  const isDisabled = useMemo(() => {
    if (!formContext.values.tableDomesticPartnership?.body?.find((item) => item.isActive)) {
      return true;
    }

    return false;
  }, [formContext.values.tableDomesticPartnership?.body]);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  return (
    <PmiDialog
      fullScreen={fullScreen}
      open={open}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <PmiDialogTitle id="alert-dialog-title" backgroundColor="primary" color="white">
        Ricerca convivenza
      </PmiDialogTitle>
      <DialogContent style={{ padding: '30px 24px' }}>
        <SearchMessages
          type={MessageType.Error}
          title={errorMessage.title || ''}
          messages={errorMessage.messages || []}
          visibility={errorMessage.visibility || false}
          onChangeVisibility={onVisibility}
        />
        <SearchMessages
          type={MessageType.Warning}
          title={
            castedAnprData?.problems?.warnings && castedAnprData?.problems?.warnings?.length > 1
              ? `Ci sono ${castedAnprData?.problems?.warnings?.length} warning`
              : `C'è 1 warning`
          }
          messages={castedAnprData?.problems?.warnings || []}
          visibility={
            visibility.warning !== undefined
              ? visibility.warning
              : !!(
                  castedAnprData?.problems &&
                  castedAnprData?.problems?.warnings &&
                  castedAnprData?.problems?.warnings?.length > 0
                )
          }
          onChangeVisibility={onVisibility}
        />
        <Filter onSearch={onSearch} formContext={formContext} />
        <TableDomesticPartnership
          domesticPartnership={castedAnprData?.body}
          formContext={formContext}
          typeRegister={typeRegister}
        />
      </DialogContent>
      <DialogActions>
        <PmiButton
          variant="contained"
          color="primary"
          disabled={isDisabled}
          onClick={() => formContext.handleSubmit()}
        >
          Conferma
        </PmiButton>
        <PmiButton variant="outlined" color="primary" type="button" onClick={() => onClose(true)}>
          Chiudi
        </PmiButton>
      </DialogActions>
    </PmiDialog>
  );
};

ModalDomesticPartnershipComponent.displayName = 'Modal Domestic Partnership';

const ModalDomesticPartnership = memo(ModalDomesticPartnershipComponent);

export default ModalDomesticPartnership;
